<svelte:options tag={null} />

<script lang="ts">
  import {
    parsePhoneNumber,
    formatIncompletePhoneNumber,
  } from 'libphonenumber-js';
  import type { CountryItems } from './phonenumber.types';
  import { EventHandler } from '../../utilities/EventHandler';
  export let phonenumber: string;
  export let placeholder = '0102030405';
  export let buttonId = 'dropdown_country';
  export let listId = 'phone_number_list';
  export let countries: string;
  export let selectedcountry: string;
  export let isvalid: boolean;
  export let isinvalid: boolean;

  let isCountriesListOpened = false;
  let maxlength = 25;
  let eventHandler = new EventHandler();
  let phoneNumber: string;

  $: selectedCountry = selectedcountry
    ? JSON.parse(selectedcountry)
    : ({
        isd: '+33',
        name: 'France',
        shortName: 'FR',
      } as CountryItems);

  $: parsedCountries = countries
    ? (JSON.parse(countries) as CountryItems[])
    : [];

  $: phoneNumber = phonenumber ? getNumber() : '';

  function getNumber(): string {
    return parsePhoneNumber(
      phonenumber,
      selectedCountry.shortName,
    ).formatNational();
  }

  function dispatchEvent(
    eventName: string,
    data?: string | CountryItems,
  ): void {
    eventHandler.dispatch(eventName, data);
  }

  function toggleCountriesList(): void {
    isCountriesListOpened = !isCountriesListOpened;
  }

  function openCountriesList() {
    dispatchEvent('open-countries-list');
    isCountriesListOpened = true;
  }

  function closeCountriesList() {
    dispatchEvent('close-countries-list');
    isCountriesListOpened = false;
  }

  function hasValue(): boolean {
    return phoneNumber !== '';
  }

  function changeCountry(country: CountryItems): void {
    selectedCountry = country;
    toggleCountriesList();
    dispatchEvent('change-country', country);
    if (hasValue()) {
      formatPhoneNumber();
    }
  }

  function formatPhoneNumber(event?: FocusEvent): void {
    event?.stopPropagation();
    const phoneNumberValue = event
      ? (event.target as HTMLInputElement).value
      : phoneNumber;

    if (!phoneNumberValue) {
      dispatchEvent('blur');
      return;
    }

    try {
      const formatInternational: string = parsePhoneNumber(
        phoneNumberValue,
        selectedCountry.shortName,
      ).formatInternational();

      dispatchEvent('blur', formatInternational);
    } catch (error) {
      const formatIncomplete: string = formatIncompletePhoneNumber(
        phoneNumberValue,
        selectedCountry.shortName,
      );

      dispatchEvent('blur', formatIncomplete);
    }

    phoneNumber = parsePhoneNumber(
      phoneNumberValue,
      selectedCountry.shortName,
    ).formatNational();

    if (event && event.target) {
      (event.target as HTMLInputElement).value = parsePhoneNumber(
        phoneNumberValue,
        selectedCountry.shortName,
      ).formatNational();
    }
  }

  function cleanFormat(event: FocusEvent) {
    const eventTargetValue = (event.target as HTMLInputElement).value;
    phoneNumber = formatIncompletePhoneNumber(eventTargetValue);
  }

  function initKeyboardNav(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        if (!isCountriesListOpened) {
          openCountriesList();
        }

        /* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-assignment */
        const tmpIndex: number = parsedCountries.findIndex(
          (country) => country.isd === selectedCountry.isd,
        );
        let index = event.key === 'ArrowDown' ? tmpIndex + 1 : tmpIndex - 1;

        if (index === -1 || index >= parsedCountries.length) {
          index = index === -1 ? parsedCountries.length - 1 : 0;
        }

        selectedCountry = parsedCountries[index];
        /* eslint-enable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-assignment */
        break;
      }
      case 'Enter':
        isCountriesListOpened
          ? changeCountry(selectedCountry)
          : openCountriesList();
        break;
      case 'Esc':
        closeCountriesList();
        break;
    }
  }
</script>

<div
  class="mc-phone-number
  {isCountriesListOpened ? ' mc-phone-number--focused' : ''}"
>
  <div
    class="mc-phone-number__dropdown"
    on:keydown={initKeyboardNav}
    {...$$restProps}
  >
    <button
      id={buttonId}
      type="button"
      aria-haspopup="listbox"
      aria-labelledby={buttonId}
      class="mc-phone-number__button"
      aria-expanded={isCountriesListOpened}
      on:click={toggleCountriesList}
    >
      <span
        class="flag flag:{selectedCountry.shortName}"
        style="--CountryFlagIcon-height:1rem">&nbsp;</span
      >
      <span class="mc-phone-number__indicator">{selectedCountry.isd}</span>
    </button>
    {#if isCountriesListOpened}
      <ul
        id={listId}
        tabindex="-1"
        role="listbox"
        aria-labelledby={listId}
        class="mc-phone-number__list"
        aria-activedescendant={selectedCountry.isd}
      >
        {#each parsedCountries as country}
          <li
            role="option"
            class="mc-phone-number__item
            {country.isd === selectedCountry.isd
              ? 'mc-phone-number__item--focused'
              : ''}"
            aria-selected={country.isd === selectedCountry.isd}
            on:click={() => changeCountry(country)}
            on:keyup={(e) => {
              /* eslint-disable @typescript-eslint/no-unsafe-member-access */
              return e.key === 'Enter' && changeCountry(country);
            }}
          >
            <span
              class="flag flag:{country.shortName}"
              style="--CountryFlagIcon-height:1rem"
            >
              &nbsp;
            </span>
            <span class="mc-phone-number__country">{country.name}</span>
            &nbsp;
            <span>{country.isd}</span>
          </li>
        {/each}
      </ul>
    {/if}
  </div>
  <input
    id="smallField"
    type="tel"
    class="mc-phone-number__input mc-text-input mc-text-input--m mc-field__input"
    name="phone-number-input"
    spellcheck="false"
    class:is-valid={isvalid}
    class:is-invalid={isinvalid}
    {placeholder}
    {maxlength}
    on:blur={formatPhoneNumber}
    on:focus={cleanFormat}
    bind:value={phoneNumber}
  />
</div>

<style lang="scss">
  @import 'country-flag-icons/3x2/flags';
  @import '@mozaic-ds/styles/settings-tools/all-settings';
  @import '@mozaic-ds/styles/components/c.text-input';
  @import '@mozaic-ds/styles/components/c.phone-number';
  // Custom CSS
  .mc-phone-number {
    max-width: none;

    &__indicator,
    &__country {
      padding-left: 1rem;
    }

    &__overlay {
      position: fixed;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      background: transparent;
      z-index: 10;
    }
  }
</style>
